
import { Injectable, InjectionToken, inject } from '@angular/core';


export enum StorageKey {
  CURRENT_DEVICE_ID = 'currentDeviceId',
  CURRENT_ENVIRONMENT = 'currentEnvironment',
  CURRENT_HUB_NAME = 'currentHubName',
  CURRENT_STACK = 'currentStack',
  SELECTED_TIMEZONE = 'selectedTimezone',
  LAST_DEVICE_UID = 'lastDeviceUId',
}

export type StorageScope = 'session' | 'local';
export type StorageStrategy = StorageScope | 'prefer-session' | 'prefer-local';


export const STORAGE_DEFAULT_STRATEGY = new InjectionToken<StorageStrategy>(
  'STORAGE_DEFAULT_STRATEGY',
  { factory: () => 'prefer-session' }
);

@Injectable({ providedIn: 'root' })
export class AppStorageService {
  private readonly defaultStrategy = inject(STORAGE_DEFAULT_STRATEGY);

  // Guard against SSR or very locked-down browsers
  private hasWindow(): boolean {
    return typeof window !== 'undefined' && !!window;
  }

  private isAvailable(store: Storage | undefined): boolean {
    if (!store) return false;
    try {
      const k = '__stg_probe__';
      store.setItem(k, '1');
      store.removeItem(k);
      return true;
    } catch {
      return false;
    }
  }

  private getStore(scope: StorageScope): Storage | null {
    if (!this.hasWindow()) return null;
    const s = scope === 'session' ? window.sessionStorage : window.localStorage;
    return this.isAvailable(s) ? s : null;
    }

  // ---------- String API ----------

  getString(
    key: StorageKey,
    strategy: StorageStrategy = this.defaultStrategy
  ): string | null {
    if (!this.hasWindow()) return null;

    const order: StorageScope[] =
      strategy === 'prefer-session' ? ['session', 'local'] :
      strategy === 'prefer-local'  ? ['local', 'session'] :
      [strategy];

    for (const scope of order) {
      const store = this.getStore(scope);
      if (store) {
        const v = store.getItem(key);
        if (v !== null) return v;
      }
    }
    return null;
  }

  setString(key: StorageKey, value: string, scope: StorageScope = 'session'): void {
    const store = this.getStore(scope);
    if (store) store.setItem(key, value);
  }

  // Remove from a specific scope, or both if scope omitted
  remove(key: StorageKey, scope?: StorageScope): void {
    if (scope) {
      this.getStore(scope)?.removeItem(key);
      return;
    }
    this.getStore('session')?.removeItem(key);
    this.getStore('local')?.removeItem(key);
  }

  clear(scope: StorageScope = 'session'): void {
    this.getStore(scope)?.clear();
  }

  // ---------- JSON helpers ----------

  setJSON<T>(key: StorageKey, value: T, scope: StorageScope = 'session'): void {
    this.setString(key, JSON.stringify(value), scope);
  }

  getJSON<T>(key: StorageKey, strategy: StorageStrategy = this.defaultStrategy): T | null {
    const raw = this.getString(key, strategy);
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
}
